import { z } from 'zod';
import { entityCreatedAtSchema } from '../../utils';

/**
 * When a block shows on the page. Seven condition kinds live on the host, and
 * this is a page-level rule rather than a block setting, so v0 carries it
 * verbatim rather than remodelling it — the same treatment the section gave its
 * theme-or-literal colour string.
 */
export const blockDisplayConditionSchema = z.object({
    state: z.string(),
    operator: z.string(),
    conditions: z.array(z.record(z.string(), z.unknown())),
});

/** The header or footer a block is, or was copied from. */
export const blockRoleSchema = z.enum(['header', 'footer']);

/**
 * Who the block is, where it came from and when it shows — as opposed to
 * `props`, which is how it looks.
 *
 * The three sharing keys are absent rather than `false`/`null` on an ordinary
 * block. `role` is the part a *copy* of the header keeps — it is still a header;
 * `isShared` and `hiddenOnPages` belong to the one site-wide instance only.
 */
export const blockMetaSchema = z.object({
    /**
     * The element id the block renders with, so a link can point at it. The user
     * can rename it; unset means the host falls back to a slice of the uuid.
     */
    displayId: z.string().nullable(),
    role: blockRoleSchema.optional(),
    isShared: z.literal(true).optional(),
    /** Page ids the shared block is switched off on. */
    hiddenOnPages: z.array(z.string()).optional(),
    createdAt: entityCreatedAtSchema,
    /** The rules that decide whether the block renders for a given visitor; `null` means always. */
    displayCondition: blockDisplayConditionSchema.nullable().default(null),
});

/**
 * Envelope every block subtype shares. Only infrastructure lives here — the
 * subtype's own settings go in `props`, the way grid, tab, section and column
 * are laid out.
 *
 * A block has no `parentId`: it sits directly on the page, which is the one
 * structural difference from every other entity in this package.
 *
 * `children` holds section ids in the store and whole section objects while the
 * structure adapter still has the denormalized tree, hence `unknown[]`.
 */
export const blockBaseSchema = z.object({
    id: z.string(),
    children: z.array(z.unknown()),
    entityType: z.literal('block'),
    version: z.number().int().nonnegative(),

    meta: blockMetaSchema,
});
